/**
 * OverlayMode — legacy 카카오맵 이벤트를 React와 연결하는 팩토리 함수
 *
 * DiscoSubPageModeParams를 생성하고, 맵 이벤트(클릭, 줌, 드래그)와
 * 마커 렌더링/클릭 콜백을 설정한다.
 *
 * @param {Object} opts
 * @param {() => Object} opts.getPayload        — 현재 필터+맵 바운드 payload
 * @param {Function}     opts.onSelectGroup      — 마커 클릭 시 그룹 데이터 로드 (single/cluster)
 * @param {Function}     opts.drawPolygon        — 매물 폴리곤 그리기
 * @param {Function}     opts.onMapInteraction   — 맵 조작 시 콜백 (바텀시트 닫기 등)
 * @param {Function}     opts.getMarkerContent   — 마커 클래스 (ExplorerMarker 또는 ExplorerMarkerMobile)
 * @param {Function}     opts.getMapService      — () => estateExploreService
 */
import legacy, {
  clear_map,
  clear_land_polygons,
  clear_arch_polygons,
  g_price_markers,
} from "../../../utils/legacy";
import { mergeOverlappingMarkers } from "./markerOverlap";

// 마커 클릭 시 기존 마커 복원 + 클릭된 마커 활성화
function click_sale_marker(group_key, MarkerClass) {
  Object.keys(g_price_markers).forEach((key) => {
    const m = g_price_markers[key];
    if (m?.marker?.setContent) {
      if (m.type === "cluster") {
        m.marker.setContent(MarkerClass.cluster(m));
        m.marker.setZIndex(3);
      } else {
        m.marker.setContent(MarkerClass.default(m));
        m.marker.setZIndex(3);
      }
    }
  });

  const clicked = g_price_markers[group_key];
  if (clicked?.marker?.setContent) {
    clicked.marker.setContent(MarkerClass.clicked(clicked));
    clicked.marker.setZIndex(10);
  }
}

function click_cluster_marker(clusterId, MarkerClass) {
  Object.keys(g_price_markers).forEach((key) => {
    const m = g_price_markers[key];
    if (m?.marker?.setContent) {
      if (m.type === "cluster") {
        m.marker.setContent(MarkerClass.cluster(m));
        m.marker.setZIndex(3);
      } else {
        m.marker.setContent(MarkerClass.default(m));
        m.marker.setZIndex(3);
      }
    }
  });

  const clicked = g_price_markers[clusterId];
  if (clicked?.marker?.setContent) {
    clicked.marker.setContent(MarkerClass.cluster_clicked(clicked));
    clicked.marker.setZIndex(10);
  }
}

export function createOverlayMode({
  getPayload,
  onSelectGroup,
  onOpenDetail,
  onZoomRequired,
  drawPolygon,
  onMapInteraction,
  onMapCount,
  MarkerClass,
  getMapService,
  isSideviewOpen,
}) {
  const dspmp = new legacy.DiscoSubPageModeParams();

  // 현재 클릭된 마커 키 (reload 후 클릭 상태 복원용)
  let clickedMarkerKey = null;
  let clickedMarkerType = null; // "single" | "cluster"

  // 이전 응답 타입 추적 (cluster ↔ single 전환 시 clear 필요)
  let lastResultType = null;

  // panTo 후 idle 이벤트에서 마커 새로고침
  function reloadOnceOnPanTo() {
    const mapInstance = legacy.map;
    const handleIdleOnce = () => {
      legacy.reload();
      mapInstance.removeListener("idle", handleIdleOnce);
    };
    mapInstance.addListener("idle", handleIdleOnce);
  }

  function clickSingleMarker(group_key, suid) {
    const markerData = g_price_markers[group_key];

    clickedMarkerKey = group_key;
    clickedMarkerType = "single";
    click_sale_marker(group_key, MarkerClass);

    // 마커 위치로 지도 이동
    if (markerData?.marker?.getPosition) {
      reloadOnceOnPanTo();
      legacy.discoMap.panTo(markerData.marker.getPosition());
    }

    // 리스트 변경
    onSelectGroup({
      conditions: [{ type: "group", group_key }],
    });

    // 단일 매물(count=1)이고 suid가 있으면 상세페이지도 열기
    if (markerData?.count == 1 && suid && onOpenDetail) {
      onOpenDetail(suid, markerData);
    }
  }

  function clickClusterMarker(clusterId) {
    // 기존 단일 마커 폴리곤 지우기
    clear_land_polygons?.();
    clear_arch_polygons?.();

    const marker = g_price_markers[clusterId];

    if (!marker) return;

    clickedMarkerKey = clusterId;
    clickedMarkerType = "cluster";

    // 클러스터 위치로 지도 이동
    const latlng = new legacy.daum.maps.LatLng(marker.lat, marker.lng);
    legacy.discoMap.panTo(latlng);

    if (marker?._merged) {
      // 프론트 겹침 병합 클러스터
      onSelectGroup({ conditions: marker._merged });
    } else if (marker?.regionCode) {
      // 행정구역 클러스터 → region 조건 (bounds는 기본 필터 파라미터로 전달됨)
      onSelectGroup({
        conditions: [{ type: "region", region_code: marker.regionCode }],
      });
    } else if (marker?.sw_lat != null) {
      // 격자 클러스터 → bounds 조건 (하위호환)
      onSelectGroup({
        conditions: [{ type: "bounds", sw_lat: marker.sw_lat, sw_lng: marker.sw_lng, ne_lat: marker.ne_lat, ne_lng: marker.ne_lng }],
      });
    } else {
      // 서버 그룹 (같은 위치 여러 매물)
      onSelectGroup({
        conditions: [{ type: "group", group_key: clusterId }],
      });
    }

    click_cluster_marker(clusterId, MarkerClass);
  }

  // ── 카카오맵 이벤트 ──

  dspmp.kakaoEvent.mapClick = function () {
    clickedMarkerKey = null;
    clickedMarkerType = null;
    if (onMapInteraction) onMapInteraction();
  };

  dspmp.kakaoEvent.zoom_changed = () => {
    if (onMapInteraction) onMapInteraction();
    // sideview(상세페이지)가 열려있으면 폴리곤 유지
    if (!isSideviewOpen?.()) {
      clear_land_polygons?.();
      clear_arch_polygons?.();
    }
    // zoom 변경 시에도 reload의 diff가 처리하므로 clear_map 생략
    legacy.reload();
  };

  dspmp.kakaoEvent.dragend = function () {
    if (onMapInteraction) onMapInteraction();
    if (!isSideviewOpen?.()) {
      clear_land_polygons?.();
      clear_arch_polygons?.();
    }
    legacy.reload();
    legacy.drag_end = new Date().getTime();
  };

  // ── 마커 클릭 이벤트 ──

  dspmp.discoMarkerClickEvent.stagePin = function (selector) {
    const $selector = legacy.jQuery(selector);
    const uuid = $selector.data("uuid");
    const suid = $selector.data("suid");

    if (g_price_markers.hasOwnProperty(uuid)) {
      const marker_type = $selector.data("type");
      if (marker_type === "single") {
        clickSingleMarker(uuid, suid);
      } else if (marker_type === "cluster") {
        clickClusterMarker(uuid);
      }
    }
  };

  dspmp.discoMarkerClickEvent.mapAgentMarker = () => {};

  // ── 맵 데이터 이벤트 ──

  dspmp.discoMapEvent.addPriceMarker = function (data) {
    const r = data[0];

    // 클릭된 마커와 같은 키면 클릭 상태로 렌더링
    const isClicked = clickedMarkerKey && r.u === clickedMarkerKey;
    let content;
    if (isClicked && clickedMarkerType === "cluster") {
      content = MarkerClass.cluster_clicked(r);
    } else if (isClicked) {
      content = MarkerClass.clicked(r);
    } else {
      content = r.type === "cluster" ? MarkerClass.cluster(r) : MarkerClass.default(r);
    }

    const overlay = new legacy.daum.maps.CustomOverlay({
      position: new legacy.daum.maps.LatLng(r.lat, r.lng),
      clickable: false,
      content: content,
      zIndex: isClicked ? 10 : 3,
      xAnchor: 0.5,
      yAnchor: 1,
    });
    overlay.setMap(legacy.map);
    return overlay;
  };

  dspmp.discoMapEvent.currentAgentMarker = () => {};

  let mapController = null;
  dspmp.discoMapEvent.reload = async function () {
    if (mapController) {
      mapController.abort();
    }
    mapController = new AbortController();

    const payload = getPayload();
    const service = getMapService();

    try {
      const result = await service.getMapV2(payload, mapController.signal);
      if (result == null) return;

      // 줌 부족 — 마커/리스트 표시 안함, 토스트 표시
      if (result.type === "zoom_required") {
        clear_map();
        if (onMapCount) onMapCount(0);
        if (onZoomRequired) onZoomRequired(true);
        return;
      }
      if (onZoomRequired) onZoomRequired(false);

      if (legacy.timer_redraw != null) {
        clearTimeout(legacy.timer_redraw);
        legacy.timer_redraw = null;
      }
      if (legacy.timer_area_redraw != null) {
        clearTimeout(legacy.timer_area_redraw);
        legacy.timer_area_redraw = null;
      }

      // 현재 화면의 매물 수 계산
      const visibleCount = result.data.reduce((sum, m) => sum + (m.count || 1), 0);
      if (onMapCount) onMapCount(visibleCount);

      // 응답 타입 전환 시 (cluster ↔ single) 기존 마커 전부 정리
      if (lastResultType && lastResultType !== result.type) {
        clear_map();
      }
      lastResultType = result.type;

      if (result.type === "cluster") {
        // 서버 격자 클러스터 — diff로 깜빡임 방지
        const newKeys = new Set();
        const toAdd = []; // 새로 추가할 마커 (배치 렌더링)

        for (const m of result.data) {
          newKeys.add(m.u);
          const existing = g_price_markers[m.u];
          if (existing) {
            if (existing.count !== m.count) {
              // count 변경 → content 교체 + 위치 갱신
              const isClicked = clickedMarkerKey && m.u === clickedMarkerKey;
              const content = isClicked
                ? MarkerClass.cluster_clicked(m)
                : MarkerClass.cluster(m);
              existing.marker.setContent(content);
              existing.marker.setPosition(new legacy.daum.maps.LatLng(m.lat, m.lng));
              g_price_markers[m.u] = { ...m, marker: existing.marker };
            }
          } else {
            toAdd.push(m);
          }
        }

        // 사라진 마커 제거
        for (const key of Object.keys(g_price_markers)) {
          if (!newKeys.has(key)) {
            g_price_markers[key].marker?.setMap?.(null);
            delete g_price_markers[key];
          }
        }

        // 새 마커 배치 렌더링 (레거시 redraw와 동일한 setTimeout 방식)
        (function drawBatch(idx) {
          const BATCH_SIZE = 2;
          let count = 0;
          while (idx < toAdd.length && count < BATCH_SIZE) {
            const m = toAdd[idx];
            const overlay = dspmp.discoMapEvent.addPriceMarker([m]);
            g_price_markers[m.u] = { ...m, marker: overlay };
            idx++;
            count++;
          }
          if (idx < toAdd.length) {
            legacy.timer_redraw = setTimeout(() => drawBatch(idx), 0);
          }
        })(0);
      } else {
        // 이전 merged 마커 제거 (키가 매번 바뀌므로 redraw가 정리 못함)
        const gpm = window.g_price_markers || g_price_markers;
        for (const key of Object.keys(gpm)) {
          if (key.startsWith("merged_")) {
            gpm[key]?.marker?.setMap?.(null);
            delete gpm[key];
          }
        }
        // 개별 마커 — 레거시 redraw가 bounds 밖 제거 + 키 중복 스킵 처리
        const processed = mergeOverlappingMarkers(result.data);
        legacy.redraw(processed, 0);
      }
    } catch (err) {
      // AbortError는 정상적인 취소이므로 무시
    }
  };

  return dspmp;
}
